export interface WidgetConfig {
  tenant_id: string;
  tenant_name: string;
  mode: "developer" | "shop";
  widget: {
    subtitle?: string;
    placeholder?: string;
    welcomeMessage?: string;
    disclaimer?: string;
    suggestions?: string[];
    consent?: { intro?: string; sections?: Array<{ title: string; text: string }>; summary?: string[] };
    primaryColor?: string;
    position?: "bottom-right" | "bottom-left";
    theme?: "light" | "dark" | "auto";
    /** The office's WhatsApp, offered with the contact form. */
    whatsapp?: string;
  };
  logo_url: string | null;
  branding: boolean;
  features: { leadCapture?: boolean; leadCaptureAfterMessages?: number; [key: string]: unknown };
  /** Cloudflare Turnstile before a conversation; null when the API does not use it. */
  turnstile?: { siteKey: string; action: string } | null;
  /** Hosts answers may link to; other links show as plain text (null: no filter). */
  link_hosts?: string[] | null;
}

/** Cards replayed from the API's answer cache may come without the optional fields. */
export interface PropertyCard {
  id: string;
  /** Raw title from the source; shown only when `name` is missing. */
  title: string;
  /** "Mieszkanie 12", "Lokal usługowy U1". */
  name?: string;
  /** The investment's label, "Biernackiego 17". */
  investment?: string | null;
  floor?: number | null;
  city: string | null;
  district: string | null;
  price: number | null;
  rooms: number | null;
  area: number | null;
  images: string[];
  /** Floor plan image, opened full size in the frame. */
  plan?: string | null;
  /** The unit's PDF sheet, opened in a new tab. */
  plan_pdf?: string | null;
  url: string | null;
}

export interface ProductCard {
  kind: "product";
  id: string;
  name: string;
  price: number | null;
  currency: string;
  in_stock: boolean;
  cas_number: string | null;
  grade: string | null;
  business_only: boolean;
  url: string | null;
  image: string | null;
  packaging: string[];
  add_to_cart_url: string | null;
  safety_sheet_url: string | null;
}

export type Card = PropertyCard | ProductCard;

/** A button under an answer (API chat/followups.ts): a question to send, or the contact form. */
export interface FollowUp {
  label: string;
  message?: string;
  action?: "lead";
}

export const isProduct = (card: Card): card is ProductCard => (card as ProductCard).kind === "product";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  cards?: Card[];
  /** streaming: still arriving; failed: the question did not get an answer. */
  status?: "streaming" | "failed";
  error?: string;
  /** The failure may pass on a second try (network, limits, outage), unlike a blocked origin. */
  retryable?: boolean;
  /** Next steps offered under this answer; shown only under the last one. */
  followUps?: FollowUp[];
  /** The answer's id in the API (SSE start), which a rating refers to. */
  serverId?: string;
  /** The visitor's thumbs up or down. */
  rating?: Rating;
}

export type Rating = "up" | "down";

export type SseEvent =
  | { type: "start"; conversation_id: string; message_id?: string }
  | { type: "chunk"; content: string }
  | { type: "sources"; properties?: PropertyCard[]; products?: ProductCard[] }
  | { type: "done"; tokens_used?: number }
  | { type: "error"; message: string }
  /** The answer so far must go: the API withheld it (it quoted the assistant's instructions). */
  | { type: "retract"; content: string }
  | { type: "suggestions"; items: FollowUp[] };
